import React from 'react';
import { Link } from 'react-router-dom';

import SubtleBadge from 'components/common/SubtleBadge';
import paths from 'routes/paths';
import { TIME_OPTION } from 'constants/time';
import {
  getStocktakingBadgeColor,
  getScopeBadgeColor,
  STOCKTAKING_STATUS_LABEL,
  STOCKTAKING_SCOPE_LABEL,
} from 'constants/stocktaking/status';

export const getStocktakingListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'stocktaking_code',
    header: 'Code',
    enableSorting: true,
    meta: { valueType: 'string' },
    cell: ({ row: { original } }) => (
      <Link to={paths.stocktakings.details(tenant, warehouse, original.id)}>
        <strong>{original.stocktaking_code}</strong>
      </Link>
    ),
  },
  {
    accessorKey: 'scope',
    header: 'Scope',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => (
      <SubtleBadge pill bg={getScopeBadgeColor(original.scope)}>
        {STOCKTAKING_SCOPE_LABEL[original.scope] || original.scope}
      </SubtleBadge>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: true,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => (
      <SubtleBadge pill bg={getStocktakingBadgeColor(original.status)}>
        {STOCKTAKING_STATUS_LABEL[original.status] || original.status}
      </SubtleBadge>
    ),
  },
  {
    accessorKey: 'created_by',
    header: 'Created By',
    enableSorting: false,
    meta: { valueType: 'string' },
    cell: ({ row: { original } }) => (
      <span>{original.created_by?.email || '-'}</span>
    ),
  },
  {
    accessorKey: 'upload_count',
    header: 'Uploads',
    enableSorting: false,
    meta: {
      valueType: 'number',
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => <span>{original.upload_count}</span>,
  },
  {
    accessorKey: 'completed_at',
    header: 'Completed At',
    enableSorting: true,
    meta: { valueType: 'string' },
    cell: ({ row: { original } }) => (
      <span>
        {original.completed_at
          ? new Date(original.completed_at).toLocaleString('en-IN', TIME_OPTION)
          : '-'}
      </span>
    ),
  },
  {
    accessorKey: 'created',
    header: 'Created',
    enableSorting: true,
    meta: { valueType: 'string' },
    cell: ({ row: { original } }) => (
      <span>
        {new Date(original.created).toLocaleString('en-IN', TIME_OPTION)}
      </span>
    ),
  },
];

export const getStocktakingLineColumns = () => [
  {
    accessorKey: 'shelf_code',
    header: 'Shelf',
    enableSorting: false,
    meta: { valueType: 'string' },
    cell: ({ row: { original } }) => <span>{original.shelf_code}</span>,
  },
  {
    accessorKey: 'sku',
    header: 'SKU',
    enableSorting: false,
    meta: { valueType: 'string' },
    cell: ({ row: { original } }) => <span>{original.sku}</span>,
  },
  {
    accessorKey: 'product_name',
    header: 'Product Name',
    enableSorting: false,
    meta: {
      valueType: 'string',
      truncate: true,
      minWidth: '200px',
      maxWidth: '200px',
    },
    cell: ({ row: { original } }) => (
      <span className="d-block text-truncate" title={original.product_name}>
        {original.product_name}
      </span>
    ),
  },
  {
    accessorKey: 'system_quantity',
    header: 'System Qty',
    enableSorting: false,
    meta: {
      valueType: 'number',
      headerProps: { className: 'text-end' },
      cellProps: { className: 'text-end' },
    },
    cell: ({ row: { original } }) => <span>{original.system_quantity}</span>,
  },
  {
    accessorKey: 'system_quantity_allocated',
    header: 'Allocated',
    enableSorting: false,
    meta: {
      valueType: 'number',
      headerProps: { className: 'text-end' },
      cellProps: { className: 'text-end' },
    },
    cell: ({ row: { original } }) => (
      <span>{original.system_quantity_allocated}</span>
    ),
  },
  {
    accessorKey: 'counted_quantity',
    header: 'Counted Qty',
    enableSorting: false,
    meta: {
      valueType: 'number',
      headerProps: { className: 'text-end' },
      cellProps: { className: 'text-end' },
    },
    cell: ({ row: { original } }) => (
      <span>
        {original.counted_quantity !== null ? original.counted_quantity : '—'}
      </span>
    ),
  },
  {
    accessorKey: 'diff',
    header: 'Diff',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-end' },
      cellProps: { className: 'text-end' },
    },
    cell: ({ row: { original } }) => {
      if (original.counted_quantity === null) return <span>—</span>;
      const diff = original.counted_quantity - original.system_quantity;
      if (diff > 0)
        return <span className="text-primary fw-semibold">+{diff}</span>;
      if (diff < 0)
        return <span className="text-danger fw-semibold">{diff}</span>;
      return <span className="text-secondary">0</span>;
    },
  },
  {
    accessorKey: 'safe_prediction_result',
    header: 'Predicted Changes (Phase 1)',
    enableSorting: false,
    meta: {
      minWidth: '220px',
      maxWidth: '280px',
    },
    cell: ({ row: { original } }) => {
      if (original.counted_quantity === null)
        return <span className="text-secondary">—</span>;
      if (!original.safe_prediction_result)
        return <span className="text-secondary">No Change</span>;

      const { adjustments } = original.safe_prediction_result;
      return (
        <div className="small">
          {adjustments.map((adj, i) => {
            const diff = adj.quantity_to - adj.quantity_from;
            const diffStr =
              diff === 0 ? '' : diff > 0 ? ` (+${diff})` : ` (${diff})`;
            return (
              <div key={i}>
                PB#{adj.product_batch_id}: {adj.quantity_from}→{adj.quantity_to}
                {diffStr}
              </div>
            );
          })}
        </div>
      );
    },
  },
  {
    accessorKey: 'over_result',
    header: 'Excess (Phase 2)',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
      minWidth: '160px',
    },
    cell: ({ row: { original } }) => {
      const or = original.over_result;
      if (!or) return <span className="text-secondary">None</span>;
      if (!('rejected' in or)) {
        return <SubtleBadge bg="warning">Excess {or.excess}</SubtleBadge>;
      }
      if (or.remaining > 0) {
        return (
          <SubtleBadge bg="danger">
            {or.rejected}/{or.excess} rejected, {or.remaining} remaining
          </SubtleBadge>
        );
      }
      return (
        <SubtleBadge bg="success">
          {or.rejected}/{or.excess} rejected
        </SubtleBadge>
      );
    },
  },
];
